import React, { useState, useMemo, useEffect } from "react";
import {
  Breadcrumb,
  Layout,
  Menu,
  Skeleton,
  Divider,
  Typography,
  Grid,
} from "antd";
import styles from "layouts/base/base.module.scss";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import {
  FolderOutlined,
  UserOutlined,
  LockOutlined,
  MenuOutlined,
  LogoutOutlined,
  BookOutlined,
  ReadOutlined,
  CalendarOutlined,
  MoneyCollectOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import {
  Popup as MobilePopup,
  List as MobileList,
  SafeArea as MobileSafeArea,
  NavBar as MobileNavBar,
  Footer as MobileFooter,
  Space as MobileSpace,
  DotLoading as MobileDotLoading,
} from "antd-mobile";
import { logout } from "helpers/helpers";
import useUserStore from "stores/user";
import clsx from "clsx";

interface MenuItem {
  key: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  onClick?: () => Promise<void> | void;
  handleClick?: () => Promise<void> | void;
  children?: MenuItem[];
  tab?: string;
}

const { Header, Content, Footer, Sider } = Layout;
const { Text } = Typography;
const { useBreakpoint } = Grid;

type ComponentProps = {
  children: React.ReactNode;
};

const App: React.FC<ComponentProps> = ({ children }) => {
  const location = useLocation();
  const screens = useBreakpoint();

  const isMobile = !screens.lg;
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isMobileMenuVisible, setIsMobileMenuVisible] = useState(false);
  const [
    isCollapseAnimationInProcess,
    setIsCollapseAnimationInProcess,
  ] = useState(true);
  const { user } = useUserStore((store) => store);

  useEffect(() => {
    if (user.userData?.id) {
      setIsCollapsed(false);

      setIsCollapseAnimationInProcess(true);

      setTimeout(() => {
        setIsCollapseAnimationInProcess(false);
      }, 200);

      return;
    }

    setIsCollapsed(true);
  }, [user.userData?.id]);

  const isAddMode = useMemo(() => {
    // Проверяем, что путь заканчивается на /add или на /add/{tabName}
    if (/\/add(\/\S+)?$/.test(location?.pathname)) {
      return true;
    }
    return false;
  }, [location?.pathname]);

  const isEditMode = useMemo(() => {
    const editPageMatch = /\/edit\/(\d+)/.exec(location?.pathname);

    return editPageMatch ? editPageMatch[1] : null;
  }, [location?.pathname]);

  const basePath = useMemo(() => {
    const path = location?.pathname;

    // Убираем "/add" или "/add/tabName" в конце пути
    const pathWithoutAdd = path.replace(/\/add(?:\/\S+)?$/, "");

    // Убираем "/edit/{id}" или "/edit/{id}/tab" в конце пути
    const basePath = pathWithoutAdd.replace(/\/edit\/\d+(\/.*)?$/, "");

    return basePath;
  }, [location?.pathname]);

  /*
  const tab = useMemo(() => {
    const subRoute = location?.pathname?.startsWith(basePath + "/")
      ? location?.pathname.replace(basePath + "/", "")
      : undefined;

    if ((!isEditMode && !isAddMode) || !subRoute) return null;

    const parts = subRoute.split("/");

    if (isEditMode)
      return parts.length > 2 ? parts[parts.length - 1] : "default";

    if (isAddMode)
      return parts.length > 1 ? parts[parts.length - 1] : "default";
  }, [location?.pathname, basePath, isEditMode, isAddMode]);
  */

  const topMenuItems: MenuItem[] = useMemo(() => {
    let result: MenuItem[] = [];

    if (!user.isAuthorizationChecked) result = [];

    if (basePath === "/login") result = [];

    if (user.userData?.role === "admin")
      result = [
        {
          key: "/reference-books/stages|/reference-books",
          icon: <BookOutlined />,
          label: "Справочники",
          children: [
            {
              key: "/reference-books/stages",
              icon: <BookOutlined />,
              label: "Этапы работ",
            },
            {
              key: "/reference-books/stage-groups",
              icon: <FolderOutlined />,
              label: "Группы этапов работ",
            },
            {
              key: "/reference-books/stage-units",
              icon: <FolderOutlined />,
              label: "Единицы измерения",
            },
            {
              key: "/reference-books/citizenships",
              icon: <FolderOutlined />,
              label: "Список гражданств",
            },
            {
              key: "/reference-books/surfaces",
              icon: <FolderOutlined />,
              label: "Список типов поверхностей",
            },
          ],
        },
        {
          key: "/objects-and-clients",
          icon: <BookOutlined />,
          label: "Объекты и клиенты",
          children:
            isAddMode || isEditMode
              ? [
                  {
                    key: `/objects-and-clients/${
                      isAddMode ? "add" : `edit/${isEditMode}`
                    }`,
                    icon: <BookOutlined />,
                    label: "Общие данные",
                    tab: "default",
                  },
                  {
                    key: `/objects-and-clients/${
                      isAddMode ? "add" : `edit/${isEditMode}`
                    }/measures`,
                    icon: <BookOutlined />,
                    label: "Замеры",
                    tab: "measures",
                  },
                  {
                    key: `/objects-and-clients/${
                      isAddMode ? "add" : `edit/${isEditMode}`
                    }/masters`,
                    icon: <BookOutlined />,
                    label: "Мастера",
                    tab: "masters",
                  },
                  ...(isEditMode
                    ? [
                        {
                          key: `/objects-and-clients/edit/${isEditMode}/report`,
                          icon: <BookOutlined />,
                          label: "Отчет по объекту",
                          tab: "report",
                        },
                      ]
                    : []),
                ]
              : undefined,
        },
        {
          key: "/masters",
          icon: <BookOutlined />,
          label: "Все мастера",
        },
        {
          key: "/knowledge-base",
          icon: <ReadOutlined />,
          label: "База знаний",
        },
      ];

    if (user.userData?.role === "master")
      result = [
        {
          key: "/schedule",
          icon: <CalendarOutlined />,
          label: "Редактирование графика",
        },
        {
          key: "/finance",
          icon: <MoneyCollectOutlined />,
          label: "Финансы",
        },
        {
          key: "/knowledge-base",
          icon: <ReadOutlined />,
          label: "База знаний",
        },
        {
          key: "/object-statistics",
          icon: <BarChartOutlined />,
          label: "Статистика по объектам",
        },
      ];

    if (user.userData?.role === "senior_master")
      result = [
        {
          key: "/schedule",
          icon: <CalendarOutlined />,
          label: "Подтверждение графика",
        },
        {
          key: "/volume-confirmation",
          icon: <MoneyCollectOutlined />,
          label: "Подтверждение объема",
        },
        {
          key: "/finance",
          icon: <ReadOutlined />,
          label: "Счета от мастеров",
        },
      ];

    if (user.userData?.role === "accountant")
      result = [
        {
          key: "/finance",
          icon: <ReadOutlined />,
          label: "Оплата счетов",
        },
      ];

    result = result.map((item) => {
      return {
        ...item,
        label: isMobile ? (
          item.label
        ) : (
          <Link to={item.key.split("|")[0]}>{item.label}</Link>
        ),
        onClick: item.onClick,
        children: item?.children
          ? item.children.map((subItem) => {
              return {
                ...subItem,
                label: isMobile ? (
                  subItem.label
                ) : (
                  <Link to={subItem.key.split("|")[0]}>{subItem.label}</Link>
                ),
                onClick: subItem.onClick,
              };
            })
          : undefined,
      };
    });

    return result;
  }, [
    basePath,
    user.isAuthorizationChecked,
    user.userData,
    isMobile,
    isAddMode,
    isEditMode,
  ]);

  const userProfileMenuItem: MenuItem = useMemo(() => {
    return {
      key: "/profile",
      icon: <UserOutlined />,
      label: "Профиль",
    };
  }, []);

  const changePasswordMenuItem: MenuItem = useMemo(() => {
    return {
      key: "/change-password",
      icon: <LockOutlined />,
      label: "Сменить пароль",
    };
  }, []);

  const logoutMenuItem: MenuItem = useMemo(() => {
    return {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Выход",
      handleClick: () => {
        logout();

        setIsMobileMenuVisible(false);
      },
    };
  }, [setIsMobileMenuVisible]);

  const leftMenuItems: MenuItem[] = useMemo(() => {
    if (!user.isAuthorizationChecked) return [];

    if (basePath === "/login") return [];

    const result: MenuItem[] = [];

    const currentTopMenuItem = topMenuItems.find((topMenuItem) => {
      const paths = topMenuItem.key.split("|");

      if (paths.includes(basePath)) return true;

      if (topMenuItem.children?.length)
        return !!topMenuItem.children.find((subMenuItem) => {
          const subMenuItemPaths = subMenuItem.key.split("|");

          if (subMenuItemPaths.includes(basePath)) return true;

          return false;
        });

      return false;
    });

    if (currentTopMenuItem?.children)
      currentTopMenuItem.children.forEach((childrenItem) => {
        result.push(childrenItem);
      });

    return result;
  }, [basePath, user.isAuthorizationChecked, topMenuItems]);

  const userMenuItems: MenuItem[] = useMemo(() => {
    if (!user.isAuthorizationChecked) return [];

    if (basePath === "/login") return [];

    let result: MenuItem[] = [];

    result.push(userProfileMenuItem);
    result.push(changePasswordMenuItem);
    result.push(logoutMenuItem);

    result = result.map((item) => {
      const resultItem = { ...item };

      if (!isMobile) delete resultItem.handleClick;

      resultItem.label =
        isMobile || !!item?.handleClick ? (
          item.label
        ) : (
          <Link to={item.key.split("|")[0]}>{item.label}</Link>
        );

      if (!isMobile) resultItem.onClick = item?.handleClick;

      resultItem.children = resultItem?.children
        ? resultItem.children.map((subItem) => {
            return {
              ...subItem,
              label: isMobile ? (
                subItem.label
              ) : (
                <Link to={subItem.key.split("|")[0]}>{subItem.label}</Link>
              ),
            };
          })
        : undefined;

      return resultItem;
    });
    return result;
  }, [
    basePath,
    user.isAuthorizationChecked,
    logoutMenuItem,
    changePasswordMenuItem,
    userProfileMenuItem,
    isMobile,
  ]);

  const topMenuSelectedKeys = useMemo(() => {
    if (!user.isAuthorizationChecked) return [];

    if (basePath === "/login") return [];

    const result: string[] = [];

    const currentTopMenuItem = topMenuItems.find((topMenuItem) => {
      const paths = topMenuItem.key.split("|");

      if (paths.includes(basePath)) return true;

      if (topMenuItem.children?.length)
        return !!topMenuItem.children.find((subMenuItem) => {
          const subMenuItemPaths = subMenuItem.key.split("|");

          if (subMenuItemPaths.includes(basePath)) return true;

          return false;
        });

      return false;
    });

    if (currentTopMenuItem) result.push(currentTopMenuItem?.key);

    if (currentTopMenuItem?.children)
      currentTopMenuItem.children.forEach((subMenuItem) => {
        const subMenuItemPaths = subMenuItem.key.split("|");

        if (subMenuItemPaths.includes(basePath)) result.push(subMenuItem.key);
        if (subMenuItemPaths.includes(location?.pathname))
          result.push(subMenuItem.key);
      });

    return result;
  }, [basePath, location?.pathname, user.isAuthorizationChecked, topMenuItems]);

  const leftMenuSelectedKeys = useMemo(() => {
    if (!user.isAuthorizationChecked) return [];

    if (basePath === "/login") return [];

    const result: string[] = [];

    const currentTopMenuItem = topMenuItems.find((topMenuItem) => {
      const paths = topMenuItem.key.split("|");

      if (paths.includes(basePath)) return true;

      if (topMenuItem.children?.length)
        return !!topMenuItem.children.find((subMenuItem) => {
          const subMenuItemPaths = subMenuItem.key.split("|");

          if (subMenuItemPaths.includes(basePath)) return true;
          if (subMenuItemPaths.includes(location?.pathname)) return true;

          return false;
        });

      return false;
    });

    if (currentTopMenuItem?.children)
      currentTopMenuItem.children.forEach((subMenuItem) => {
        const subMenuItemPaths = subMenuItem.key.split("|");

        if (subMenuItemPaths.includes(basePath)) result.push(subMenuItem.key);
        if (subMenuItemPaths.includes(location?.pathname))
          result.push(subMenuItem.key);
      });

    return result;
  }, [basePath, location?.pathname, user.isAuthorizationChecked, topMenuItems]);

  const userMenuSelectedKeys = useMemo(() => {
    if (!user.isAuthorizationChecked) return [];

    if (basePath === "/login") return [];

    const result: string[] = [];

    if (userMenuItems?.length)
      userMenuItems.forEach((userMenuItem) => {
        const userMenuItemPaths = userMenuItem.key.split("|");

        if (userMenuItemPaths.includes(basePath)) result.push(userMenuItem.key);
      });

    return result;
  }, [basePath, user.isAuthorizationChecked, userMenuItems]);

  const breadcrumbs = useMemo(() => {
    if (!user.isAuthorizationChecked)
      return [
        {
          title: (
            <div className={styles.titleSkeleton}>
              <Skeleton.Input active={true} size="small" block={true} />
            </div>
          ),
        },
      ];

    const result: { title: React.ReactNode; onClick?: () => void }[] = [];

    if (user.userData?.role === "admin")
      result.push({
        title: <Link to={"/"}>Администратор</Link>,
      });

    if (user.userData?.role === "master")
      result.push({
        title: <Link to={"/"}>Мастер</Link>,
      });

    if (user.userData?.role === "senior_master")
      result.push({
        title: <Link to={"/"}>Старший мастер</Link>,
      });

    if (user.userData?.role === "accountant")
      result.push({
        title: <Link to={"/"}>Бухгалтер</Link>,
      });

    if (topMenuItems?.length) {
      let itemFound = false;

      topMenuItems.forEach((topMenuItem) => {
        if (itemFound) return;

        const topMenuItemPaths = topMenuItem.key.split("|");

        const subMenuItem = topMenuItem?.children
          ? topMenuItem.children.find((subMenuItem) => {
              const subMenuItemPaths = subMenuItem.key.split("|");

              if (subMenuItemPaths.includes(basePath)) return true;

              return false;
            })
          : undefined;

        if (subMenuItem) {
          result.push({
            title: topMenuItem.label,
          });
          result.push({
            title: subMenuItem.label,
          });

          itemFound = true;
        }

        if (itemFound) return;

        if (topMenuItemPaths.includes(basePath)) {
          result.push({
            title: topMenuItem.label,
          });

          itemFound = true;
        }
      });
    }

    if (userMenuItems?.length)
      userMenuItems.forEach((userMenuItem) => {
        const userMenuItemPaths = userMenuItem.key.split("|");

        if (userMenuItemPaths.includes(basePath))
          result.push({ title: userMenuItem.label });
      });

    if (isAddMode) {
      result.push({ title: "Добавление" });
    }

    if (isEditMode) {
      result.push({ title: "Редактирование" });
    }

    return result;
  }, [
    basePath,
    user.isAuthorizationChecked,
    user.userData,
    topMenuItems,
    userMenuItems,
    isAddMode,
    isEditMode,
  ]);

  if (isMobile)
    return (
      <>
        <MobileSafeArea position="top" />
        <MobileSpace
          direction="vertical"
          justify="between"
          className={styles.wrapperMobile}
        >
          <MobileNavBar
            back={null}
            left={
              <Link className={styles.logoMobile} to={"/"}>
                arhipov.pro
              </Link>
            }
            right={
              (!!topMenuItems.length || !!leftMenuItems.length) && (
                <MenuOutlined
                  className={styles.menuMobile}
                  onClick={() => setIsMobileMenuVisible(!isMobileMenuVisible)}
                />
              )
            }
          />
          {user.isAuthorizationChecked ? (
            children
          ) : (
            <div className={styles.loadingMobile}>
              <MobileDotLoading />
            </div>
          )}
          <MobileFooter
            content={<>ARHIPOV.PRO ©{new Date().getFullYear()}</>}
          />
        </MobileSpace>
        <MobileSafeArea position="bottom" />
        {!!user?.isAuthorized && (
          <MobilePopup
            visible={isMobileMenuVisible}
            onMaskClick={() => setIsMobileMenuVisible(!isMobileMenuVisible)}
            position="right"
            bodyClassName={styles.popupMobile}
            maskClassName={styles.popupMaskMobile}
          >
            <MobileList key={"topMenuItems"}>
              {!!topMenuItems.length &&
                topMenuItems.map((topMenuItem, topMenuItemIndex) => {
                  const paths = topMenuItem.key.split("|");

                  const isActive =
                    paths.includes(basePath) ||
                    (!!topMenuItem.children?.length &&
                      !!topMenuItem.children.find(
                        (subMenuItem, subMenuItemIndex) => {
                          const subMenuItemPaths = subMenuItem.key.split("|");

                          if (subMenuItemPaths.includes(basePath)) return true;

                          return false;
                        }
                      ));

                  return (
                    <React.Fragment key={topMenuItemIndex}>
                      <Link
                        to={paths[0]}
                        key={topMenuItemIndex}
                        onClick={() => {
                          topMenuItem?.handleClick && topMenuItem.handleClick();

                          if (topMenuItem.children?.length) return;

                          setIsMobileMenuVisible(false);
                        }}
                      >
                        <MobileList.Item
                          arrowIcon={topMenuItem.icon}
                          key={topMenuItemIndex}
                        >
                          <div
                            className={clsx(
                              styles.menuItemMobile,
                              !!isActive && styles.isActive
                            )}
                          >
                            {topMenuItem.label}
                          </div>
                        </MobileList.Item>
                      </Link>
                      {!!isActive &&
                        !!topMenuItem.children?.length &&
                        topMenuItem.children.map(
                          (subMenuItem, subMenuItemIndex) => {
                            const subMenuItemPaths = subMenuItem.key.split("|");

                            const isSubmenuActive = subMenuItemPaths.includes(
                              basePath
                            );

                            return (
                              <Link
                                to={subMenuItemPaths[0]}
                                key={subMenuItemIndex}
                                onClick={() => {
                                  setIsMobileMenuVisible(false);
                                }}
                              >
                                <MobileList.Item
                                  arrowIcon={subMenuItem.icon}
                                  key={subMenuItemIndex}
                                  onClick={subMenuItem?.handleClick}
                                >
                                  <div
                                    className={clsx(
                                      styles.subMenuItemMobile,
                                      !!isSubmenuActive && styles.isActive
                                    )}
                                  >
                                    {subMenuItem.label}
                                  </div>
                                </MobileList.Item>
                              </Link>
                            );
                          }
                        )}
                    </React.Fragment>
                  );
                })}
            </MobileList>
            {!!userMenuItems.length && (
              <MobileList key={"userMenuItems"} className={styles.userData}>
                {!isCollapsed && (
                  <MobileList.Item key={"nameAndRole"}>
                    <div className={styles.userNameMobile}>
                      {user.userData.name}
                    </div>
                    <div className={styles.userRoleMobile}>
                      {user.userData.role === "admin"
                        ? "Администратор"
                        : user.userData.role === "master"
                        ? "Мастер"
                        : user.userData.role === "accountant"
                        ? "Бухгалтер"
                        : "Старший мастер"}
                    </div>
                  </MobileList.Item>
                )}
                {userMenuItems.map((userMenuItem, userMenuItemIndex) => {
                  const userMenuItemPaths = userMenuItem.key.split("|");

                  if (userMenuItem?.handleClick)
                    return (
                      <a
                        key={userMenuItemIndex}
                        className={styles.link}
                        onClick={() => {
                          userMenuItem?.handleClick &&
                            userMenuItem.handleClick();

                          setIsMobileMenuVisible(false);
                        }}
                      >
                        <MobileList.Item
                          arrowIcon={userMenuItem.icon}
                          key={userMenuItemIndex}
                        >
                          {userMenuItem.label}
                        </MobileList.Item>
                      </a>
                    );

                  return (
                    <Link
                      to={userMenuItemPaths[0]}
                      key={userMenuItemIndex}
                      onClick={() => {
                        setIsMobileMenuVisible(false);
                      }}
                    >
                      <MobileList.Item
                        arrowIcon={userMenuItem.icon}
                        key={userMenuItemIndex}
                      >
                        {userMenuItem.label}
                      </MobileList.Item>
                    </Link>
                  );
                })}
              </MobileList>
            )}
          </MobilePopup>
        )}
      </>
    );

  return (
    <Layout className={styles.mainLayout}>
      <Layout className={styles.innerLayout}>
        <Sider
          className={styles.sider}
          collapsed={isCollapsed}
          collapsedWidth={user.userData?.id ? 80 : 0}
          width={300}
          onCollapse={(value) => {
            if (!value) {
              setIsCollapseAnimationInProcess(true);

              setTimeout(() => {
                setIsCollapseAnimationInProcess(false);
              }, 100);
            }

            setIsCollapsed(value);
          }}
        >
          <Layout className={styles.siderContent}>
            <Link className={styles.logo} to={"/"}>
              arhipov.pro
            </Link>
            <Layout className={styles.siderInformation}>
              <Layout className={styles.leftMenuBlock}>
                {!!leftMenuItems.length && (
                  <Menu
                    theme="light"
                    selectedKeys={leftMenuSelectedKeys}
                    mode="inline"
                    items={leftMenuItems}
                  />
                )}
              </Layout>
              {!!userMenuItems.length && (
                <>
                  {!isCollapsed && (
                    <Layout
                      className={clsx(
                        styles.userCard,
                        isCollapseAnimationInProcess &&
                          styles.isCollapseAnimationInProcess
                      )}
                    >
                      <Text strong>{user.userData.name}</Text>
                      <Text>
                        {user.userData.role === "admin"
                          ? "Администратор"
                          : user.userData.role === "master"
                          ? "Мастер"
                          : user.userData.role === "accountant"
                          ? "Бухгалтер"
                          : "Старший мастер"}
                      </Text>
                      <Divider className={styles.userCardDivider} />
                    </Layout>
                  )}
                  <Menu
                    theme="light"
                    selectedKeys={userMenuSelectedKeys}
                    mode="inline"
                    items={userMenuItems}
                    className={styles.userMenu}
                  />
                </>
              )}
            </Layout>
          </Layout>
        </Sider>
        <Layout
          className={clsx(
            styles.contentLayout,
            !!isCollapsed && styles.isCollapsed
          )}
        >
          <Header className={styles.header}>
            {!!topMenuItems.length && (
              <Menu
                theme="dark"
                mode="horizontal"
                selectedKeys={topMenuSelectedKeys}
                items={topMenuItems.map((topMenuItem) => ({
                  ...topMenuItem,
                  children: undefined,
                }))}
                className={styles.headerMenu}
              />
            )}
          </Header>
          <Content className={styles.content}>
            <div className={styles.breadcrumbBlock}>
              {!!breadcrumbs.length && <Breadcrumb items={breadcrumbs} />}
            </div>
            <div className={styles.contentBlock}>
              {user.isAuthorizationChecked ? (
                children
              ) : (
                <div className={styles.contentSkeleton}>
                  <Skeleton.Input active={true} size="small" block={true} />
                </div>
              )}
            </div>
          </Content>
          <Footer className={styles.footer}>
            ARHIPOV.PRO ©{new Date().getFullYear()}
          </Footer>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default App;
